import * as t from 'io-ts';
import { authorizedTo, id, meta } from '@/services/api';
import { PriorityType, PunctualityType, ReportFilterDateRange } from './constants';
import { DateFromUnixTime } from 'io-ts-types/DateFromUnixTime';
import { withFallback } from 'io-ts-types/lib/withFallback';
import { filterRecordCodec } from '@/services/api/resources/filter';

export const metricType = t.union([
  t.keyof({
    'submitted-forms': null,
    'form-score': null,
    'form-items': null,
    'negative-responses': null,
    'form-item-score': null,
    'schedule-forms': null,
    actions: null,
  }),
  t.null,
]);

export const attributeType = t.union([
  t.keyof({
    users: null,
    templates: null,
    groups: null,
    entities: null,
    folders: null,
    categories: null,
    day: null,
    week: null,
    month: null,
    quarter: null,
    year: null,
    punctuality: null,
    'action-status': null,
    'form-status': null,
    total: null,
    creator: null,
    priority: null,
    'page-section': null,
    'form-item': null,
    average: null,
    response: null,
    'approval-status': null,
    approvers: null,
  }),
  t.null,
]);

export const lineChartTimeGroupingType = t.keyof({
  day: null,
  week: null,
  month: null,
  quarter: null,
  year: null,
});

export const reportFilterDate = t.intersection([
  t.type({
    range: t.union([
      t.null,
      t.literal(ReportFilterDateRange.today),
      t.literal(ReportFilterDateRange.yesterday),
      t.literal(ReportFilterDateRange.lastWeek),
      t.literal(ReportFilterDateRange.lastSevenDays),
      t.literal(ReportFilterDateRange.lastThirtyDays),
      t.literal(ReportFilterDateRange.lastMonth),
      t.literal(ReportFilterDateRange.custom),
    ]),
    dateStart: t.union([DateFromUnixTime, t.null]),
    dateEnd: t.union([DateFromUnixTime, t.null]),
  }),
  t.partial({
    timeFrame: t.string,
    timeFrameValue: t.string,
  }),
]);

export const reportFilterValues = t.type({
  values: t.array(t.number),
});

export const reportFilterStringValues = t.type({
  values: t.array(t.string),
});

export const reportFilter = t.intersection([
  t.type({
    date: reportFilterDate,
    folder: reportFilterValues,
    entity: reportFilterValues,
    group: reportFilterValues,
    user: reportFilterValues,
    category: reportFilterValues,
    checklist: reportFilterValues,
    punctuality: t.union([t.literal(PunctualityType.tooLate), t.literal(PunctualityType.inTime), t.null]),
  }),
  t.partial({
    section: reportFilterStringValues,
    formItem: t.union([t.string, t.null]),
    response: t.union([t.string, t.null]),
    priority: t.union([t.literal(PriorityType.normal), t.literal(PriorityType.high), t.null]),
    formStatus: reportFilterValues,
    actionStatus: reportFilterValues,
    creator: reportFilterValues,
    approvalStatus: reportFilterValues,
    approvers: reportFilterValues,
    approvalDate: reportFilterDate,
  }),
]);

export const formReportWidget = t.type({
  order: t.number,
  key: t.string,
  metric: metricType,
  attribute: attributeType,
  filters: reportFilter,
  percentage: t.boolean,
  singleNumberDisplay: t.boolean,
  barChartDisplay: t.boolean,
  lineChartDisplay: t.boolean,
  pieChartDisplay: withFallback(t.boolean, false),
  tableDisplay: t.boolean,
  lineChartTimeGrouping: t.union([lineChartTimeGroupingType, t.null]),
});

export const reportWidget = t.intersection([
  t.type({ id: t.number }),
  formReportWidget,
]);

export const reportCodecData = t.intersection([
  t.type({
    title: t.string,
    filters: reportFilter,
    createdAt: DateFromUnixTime,
    updatedAt: DateFromUnixTime,
    user: t.type({
      id: t.number,
      name: t.string,
    }),
    widgets: t.array(reportWidget),
    schedule: t.union([t.number, t.null]),
  }),
  t.partial({
    obfuscatedId: t.number,
  }),
]);

export const reportCodec = t.type({
  id,
  data: reportCodecData,
});

export const listCodec = t.type({
  data: t.array(t.type({
    id,
    data: t.type({
      title: t.string,
      createdAt: DateFromUnixTime,
      updatedAt: DateFromUnixTime,
      user: t.type({
        id: t.number,
        name: t.string,
      }),
    }),
    authorizedTo,
  })),
  meta,
});

export const formCodec = t.type({
  title: t.string,
  filters: reportFilter,
  widgets: t.union([t.array(formReportWidget), t.null, t.undefined]),
});

export const formEditCodec = formCodec;

export const widgetDataPayload = t.intersection([
  t.type({
    filters: reportFilter,
    metric: metricType,
    attribute: attributeType,
    lineChartTimeGrouping: t.union([lineChartTimeGroupingType, t.null]),
    display: t.union([t.string, t.null]),
  }),
  t.partial({
    detailedView: t.boolean,
    page: t.number,
    perPage: t.number,
    searchTerm: t.string,
    response: t.union([t.string, t.null]),
  }),
]);

export const formItemFilterRecordCodec = t.type({
  id: t.string,
  title: t.string,
  type: t.string,
  checklistId: t.number,
  checklistTitle: withFallback(t.string, ''),
});

export const formItemFilterCodec = t.type({
  data: t.array(formItemFilterRecordCodec),
});

export const filterOptionsCodec = t.type({
  data: t.type({
    users: t.array(filterRecordCodec),
    groups: t.array(filterRecordCodec),
    checklists: t.array(filterRecordCodec),
    categories: t.array(filterRecordCodec),
    entityItems: t.array(filterRecordCodec),
    entityFolders: t.array(filterRecordCodec),
    approvalStatuses: t.array(filterRecordCodec),
  }),
});
